import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";

const ADMIN_EMAIL = "info@subyect.com";

/**
 * Custom hook for managing admin authentication and authorization
 *
 * Checks user authentication status and validates admin access based on email.
 * Automatically updates on auth state changes. Admin status is determined by
 * comparing the user's email to the ADMIN_EMAIL constant (info@subyect.com).
 *
 * @returns Object containing admin authentication state:
 * - `isAuthed` - Whether user is currently logged in
 * - `isAdmin` - Whether authenticated user has admin privileges
 * - `userId` - Current user's ID if authenticated, null otherwise
 *
 * @example
 * // Protect admin-only routes
 * function AdminDashboard() {
 *   const { isAuthed, isAdmin, userId } = useAdminAuth();
 *
 *   if (!isAuthed) {
 *     return <Navigate to="/login" />;
 *   }
 *
 *   if (!isAdmin) {
 *     return <AccessDenied />;
 *   }
 *
 *   return <AdminPanel userId={userId} />;
 * }
 */
export function useAdminAuth() {
  const [isAuthed, setIsAuthed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const checkAdmin = (session: Session | null) => {
      if (!session) {
        setIsAuthed(false);
        setIsAdmin(false);
        setUserId(null);
        return;
      }

      setIsAuthed(true);
      setUserId(session.user.id);

      // Check if user email matches admin email
      const userEmail = session.user.email?.toLowerCase();
      setIsAdmin(userEmail === ADMIN_EMAIL);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      checkAdmin(session);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      checkAdmin(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { isAuthed, isAdmin, userId };
}
